import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Button, Form, Icon, ListItemProps, Segment, SemanticWIDTHS, Table } from 'semantic-ui-react'
import DatepickerComponent from '../../../../components/Datepicker/Datepicker'
import DropdownInsideScrollableParent from '../../../../components/Dropdown/DropdownInsideScrollableParent'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { decimalNumberRegex } from '../../../../regex/fieldsValidationRegex'
import { currencyFormat } from '../../../../utils/currencyFormat'
import { IProjectBudgetChanges, IProjectBudgetChangesBulk } from '../../types/project'

interface Props {
  project_id: number
  project_budget: number
  openModal: boolean
  budgets: IProjectBudgetChanges[]
  renderExpAndForContent: (modal: boolean) => JSX.Element
  updateBudgetChanges: (projectId: string | number, budget: IProjectBudgetChangesBulk) => Promise<IProjectBudgetChanges[]>
  getProject: (id: string | number) => void
  onClose: () => void
}

const _header = [
  { text: '', key: 'prefix', width: '2' as SemanticWIDTHS },
  { text: 'Amount', key: 'amount', width: '3' as SemanticWIDTHS },
  { text: 'Description', key: 'description', width: '5' as SemanticWIDTHS },
  { text: 'Date', key: 'date', width: '4' as SemanticWIDTHS },
  { text: '', key: 'settings', width: '2' as SemanticWIDTHS },
]

const BudgetModalComponent = (props: Props): JSX.Element => {
  const { openModal, budgets, renderExpAndForContent, onClose, updateBudgetChanges, getProject, project_id, project_budget } = props

  const [budgetList, setBudgetList] = useState(budgets)
  const [budgetListRemove, setBudgetListRemove] = useState<number[]>([])
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [dateId, setDateId] = useState(-1)
  const [inputValues, setInputValues] = useState<string[]>([])
  const [warningMsg, setWarningMsg] = useState<string>('')

  useEffect(() => {
    const _budgets = budgets.filter(b => b.description !== 'Initial Budget')
    const _sortedBudget = _budgets.length !== 0 ? _budgets.sort((a, b) => a.date && b.date ? moment(a.date).diff(moment(b.date)) : 0) : _budgets
    _sortedBudget.unshift(budgets.filter(b => b.description === 'Initial Budget')[0])
    setBudgetList(_sortedBudget)
    const _allValues = [] as string[]
    _sortedBudget.forEach(sb => _allValues.push(sb.amount.toString().replace('.', ',')))
    setInputValues(_allValues)
  }, [budgets])

  useEffect(() => {
    const budgetSum = budgetList.map(b => b.prefix === 'increase' ? +b.amount : -Math.abs(+b.amount)).reduce((a, b) => a + b, 0) >= 0
    setWarningMsg(!budgetSum ? "Budget can't be negative" : '')
  }, [budgetList])

  const handleAddBudget = () => {
    setBudgetList(prev => [...prev, { id: '', prefix: '', amount: '', description: '', date: new Date() }])
  }

  const handleRemoveBudget = (idx: number) => {
    const newBudgets = budgetList.filter((b, i) => i !== idx)
    const budgetId = budgetList.filter((budget, i) => i === idx).map(b => b.id)[0]
    if (budgetId !== '') {
      setBudgetListRemove(prev => [...prev, +budgetId])
    }
    setBudgetList(newBudgets)
    setInputValues(inputValues.filter((iv, i) => i !== idx))
  }

  const renderCard = () => {
    return (
      <Segment style={{ display: 'flex', margin: '-2rem 0', borderRadius: '.57rem', boxShadow: 'none', width: '85%', minWidth: '25rem', height: '8.2rem', border: '3px solid #F4F7F9' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <p style={{ color: '#172533', fontSize: '1.7rem', margin: 0 }}>Available Budget</p>
            <div style={{ display: 'flex', float: 'left', color: '#08D6A1', fontWeight: 'bold', fontSize: '3rem' }}>
              <p style={{ margin: 0 }}>{currencyFormat(project_budget)[0]} {currencyFormat(project_budget).split(',')[0].replace(currencyFormat(project_budget)[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(project_budget).split(',')[1]}</p>
            </div>
          </div>
        </div>
        <hr style={{ width: 0, height: 'auto', margin: '0 3rem', opacity: .1 }} />
        {renderExpAndForContent(true)}
      </Segment>
    )
  }

  const renderTableHeader = () => {
    return _header.map((h, idx) => {
      return (
        <Table.Cell
          key={idx}
          textAlign={idx === 1 ? 'right' : 'left'}
          width={h.width}
          style={{
            fontSize: '1rem',
            backgroundColor: '#F4F7F9',
            color: '#747678',
            padding: idx === 1 || idx === 3 ? '.2rem 1rem' : '.2rem 0',
            borderRadius: idx === 0 ? '4px 0 0 4px' : idx === 4 ? '0 4px 4px 0' : '0'
          }}>{h.text}</Table.Cell>
      )
    })
  }

  const handlePrefix = (data: ListItemProps, idx: number) => {
    const newBudgetList = budgetList.map((b, i) => {
      if (i === idx) {
        b.prefix = data.value === 'increase' || data.value === 'decrease' ? data.value : ''
      }
      return b
    })
    setBudgetList(newBudgetList)
  }

  const renderPrefixDropdown = (prefix: 'increase' | 'decrease' | '', idx: number) => {
    const _options = [
      { key: '1', text: (<Icon style={{ color: '#08D6A1' }} name={'plus circle'} />), value: 'increase', selected: false },
      { key: '2', text: (<Icon style={{ color: '#FA5953' }} name={'minus circle'} />), value: 'decrease', selected: false }
    ]
    return (
      <DropdownInsideScrollableParent
        iconName='caret down'
        iconColor='#08D6A1'
        className='budget-prefix-dropdown'
        placeholderMarginLeft='-1.2rem'
        options={_options}
        placeholder='Prefix'
        content={prefix !== '' ? _options.filter(o => o.value === prefix)[0].text : ''}
        onClick={(e, data) => handlePrefix(data, idx)}
        buttonWidth='50%'
        buttonMarginLeft='.5rem'
      />
    )
  }

  const checkBudgetInput = (amount: number) => {
    return decimalNumberRegex.regexPattern.test(amount ? amount.toString() : '')
  }

  const inputChange = (event: React.ChangeEvent<HTMLInputElement>, idx: number, key: 'amount' | 'description', converted?: number) => {
    const newBudgetList = budgetList.map((b, i) => {
      if (i === idx) {
        if (key === 'amount') {
          b.amount = converted ?? 0
        } else {
          b.description = event.target.value
        }
      }
      return b
    })
    if (key === 'amount') {
      const _values = inputValues.map((iv, i) => {
        if (idx === i) {
          return event.target.value
        }
        return iv
      })
      setInputValues(_values)
    }
    const validInput = key === 'amount' ? checkBudgetInput(+event.target.value) : true
    if (validInput) {
      setBudgetList(newBudgetList)
    }
  }

  const onInputClick = (idx: number) => {
    setDateId(idx)
    if (openDatePicker) {
      setOpenDatePicker(false)
    } else {
      setOpenDatePicker(true)
    }
  }

  const changeDate = (date: Date | null, idx: number) => {
    const newBudgetList = budgetList.map((b, i) => {
      if (i === idx) {
        b.date = date
      }
      return b
    })
    setBudgetList(newBudgetList)
    setOpenDatePicker(false)
  }

  const handleSaveBudget = () => {
    const newBudgetList = budgetList.filter(b => b.id === '' && b.prefix !== '' && b.amount !== '' && b.date && b.description !== '')
    newBudgetList.map(b => {
      b.amount = b.amount === '' ? 0 : +b.amount
      b.date = moment(b.date).format('YYYY-MM-DD')
      return b
    })
    const data: IProjectBudgetChangesBulk = {
      to_create: newBudgetList,
      to_delete: budgetListRemove
    }
    updateBudgetChanges(project_id, data).then(() => getProject(project_id))
    onClose()
  }

  const handleDisabledSave = () => {
    const budgetSum = budgetList.map(b => b.prefix === 'increase' ? +b.amount : -Math.abs(+b.amount)).reduce((a, b) => a + b, 0) >= 0
    const budgetEmpty = budgetList.filter(b => !b.prefix || !b.amount || !b.description).length === 0
    return !(budgetSum && budgetEmpty)
  }

  const renderTable = () => {
    return (
      <Table className='borderless-table-header' padded columns={16} style={{ border: 'none', marginTop: '3rem' }}>
        <Table.Header>
          <Table.Row>
            {renderTableHeader()}
          </Table.Row>
        </Table.Header>
      </Table>
    )
  }

  const handleEnterButtonPress = (event: React.KeyboardEvent) => {
    const key = event.key
    key === 'Enter' ? event.preventDefault() : undefined
  }

  const renderFormFields = () => {
    return (
      budgetList.map((b, idx) => {
        return (
          <Form.Group key={idx} style={{ marginBottom: b.description === 'Initial Budget' ? '1.5rem' : '' }} onKeyDown={handleEnterButtonPress}>
            <Form.Field width={2} style={{ paddingRight: 0, textAlign: 'center' }}>
              {b.id === '' ? renderPrefixDropdown(b.prefix, idx) : <Icon style={{ color: b.prefix === 'increase' ? '#08D6A1' : '#FA5953' }} name={b.prefix === 'increase' ? 'plus circle' : 'minus circle'} />}
            </Form.Field>

            {b.id === '' ?
              <ValidationInput
                type='number'
                value={inputValues[idx]}
                onChange={(e, v, c) => inputChange(e, idx, 'amount', c)}
                placeholder='€ 0,00'
                zIndex={1}
                symbol='€'
                boldSymbol={true}
                className='right-aligned'
                symbolLeft={true}
                fieldWidth={3}
                symbolMarginLeft='6.3rem'
              /> :
              <Form.Field width={3} style={{ paddingRight: '.8rem' }}>
                <div style={{ display: 'flex', float: 'right', color: '#232C58', fontWeight: 'bold' }}>
                  <p style={{ margin: 0 }}>{currencyFormat(+b.amount)[0]} {currencyFormat(+b.amount).split(',')[0].replace(currencyFormat(+b.amount)[0], '')}</p>
                  <p style={{ color: '#BEC8C3' }}>,{currencyFormat(+parseFloat(b.amount.toString()).toFixed(2)).split(',')[1]}</p>
                </div>
              </Form.Field>}

            {b.id === '' ?
              <ValidationInput
                value={b.description}
                onChange={e => inputChange(e, idx, 'description')}
                placeholder='Enter details'
                fieldWidth={5}
                paddingLeft={0}
                paddingRight={0}
              />
              : <Form.Field width={5} style={{ paddingLeft: '.1rem', fontWeight: 'bold' }}>{b.description}</Form.Field>}

            <Form.Field width={5} style={{ paddingLeft: '1rem' }}>
              {b.id === '' ?
                <DatepickerComponent
                  className='datepicker-small-input'
                  inputBorder={true}
                  open={idx === dateId && openDatePicker}
                  onClickOutside={() => setOpenDatePicker(false)}
                  onInputClick={() => onInputClick(idx)}
                  onChange={(date: Date) => changeDate(date, idx)}
                  selected={b.date ? moment(b.date).toDate() : undefined}
                  dateFormat='dd.MM.yyyy'
                  placeholder='Enter date'
                />
                : moment(b.date).format('DD.MM.YYYY')}
            </Form.Field>

            <Form.Field width={2} style={{ paddingRight: 0 }}>
              {b.description !== 'Initial Budget' ?
                <Button icon style={{ backgroundColor: 'transparent', boxShadow: 'none', marginTop: '-.5rem' }} onClick={() => handleRemoveBudget(idx)}>
                  <Icon name='times circle' color='red' style={{ fontSize: '15px' }} />
                </Button> : null}
            </Form.Field>
          </Form.Group>
        )
      })
    )
  }

  const renderForm = () => {
    return (
      <Form style={{ position: 'inherit' }}>{renderFormFields()}</Form>
    )
  }

  const renderFooter = () => {
    return (
      <>
        {warningMsg && <div style={{ color: '#fa5953', fontSize: '12px', paddingBottom: '6px', marginTop: '-18px' }}>* {warningMsg}</div>}
        <Button disabled={handleDisabledSave()} onClick={handleSaveBudget} primary={true} size='small' content='Save' style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, margin: 0 }} />
      </>
    )
  }

  const renderContent = () => {
    return (
      <div>
        {renderCard()}
        {renderTable()}
        {renderForm()}
        <Button
          className='button-add-item'
          inverted
          size='small'
          color='teal'
          onClick={() => handleAddBudget()}
          content='Add Budget'
          icon={<Icon name='plus' />}
          style={{ marginLeft: '.4rem', width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: '0rem', color: '#08D6A1', backgroundColor: '#CDF7EC' }}
        />
      </div>
    )
  }

  const renderModal = () => {
    return (
      <WizardComponent
        open={openModal}
        close={onClose}
        content={renderContent()}
        headerColor='#232C58'
        footer={renderFooter()}
        width='50rem'
        contentPadding='2rem 1rem'
        borderTopFooter='1px solid #E8E8E8'
      />
    )
  }

  return renderModal()
}

export default BudgetModalComponent
